import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { useCallback, useMemo, useState } from "react";

export interface SetConversationSelectedInput {
  conversationId: ConversationId;
  rowIndex: number;
  checked: boolean;
  shiftKey: boolean;
}

export interface ConversationTableSelectionState {
  selectedConversationIds: ConversationId[];
  selectedConversations: ConversationListItemResponse[];
  selectedConversationCount: number;
  hoveredConversationId: ConversationId | null;
  hasAnySelectedConversations: boolean;
  setHoveredConversationId: (id: ConversationId | null) => void;
  isConversationSelected: (id: ConversationId) => boolean;
  setConversationSelected: (input: SetConversationSelectedInput) => void;
  setAllConversationsSelected: (checked: boolean) => void;
  clearSelectedConversations: () => void;
}

export function useConversationTableSelection(
  conversations: readonly ConversationListItemResponse[],
): ConversationTableSelectionState {
  const [rawSelectedConversationIds, setRawSelectedConversationIds] = useState<
    ConversationId[]
  >([]);
  const [hoveredConversationId, setHoveredConversationId] =
    useState<ConversationId | null>(null);
  const [selectionAnchorIndex, setSelectionAnchorIndex] = useState<
    number | null
  >(null);

  const visibleConversationIdSet = useMemo(
    () => new Set(conversations.map((conversation) => conversation.id)),
    [conversations],
  );

  const selectedConversationIds = useMemo(
    () =>
      rawSelectedConversationIds.filter((id) =>
        visibleConversationIdSet.has(id),
      ),
    [rawSelectedConversationIds, visibleConversationIdSet],
  );

  const selectedConversationIdSet = useMemo(
    () => new Set(selectedConversationIds),
    [selectedConversationIds],
  );

  const selectedConversations = useMemo(
    () =>
      conversations.filter((conversation) =>
        selectedConversationIdSet.has(conversation.id),
      ),
    [conversations, selectedConversationIdSet],
  );

  const selectedConversationCount = selectedConversationIds.length;
  const hasAnySelectedConversations = selectedConversationCount > 0;

  const isConversationSelected = useCallback(
    (id: ConversationId) => selectedConversationIdSet.has(id),
    [selectedConversationIdSet],
  );

  const setConversationSelected = useCallback(
    ({
      conversationId,
      rowIndex,
      checked,
      shiftKey,
    }: SetConversationSelectedInput) => {
      setRawSelectedConversationIds((previous) => {
        const next = new Set(previous);

        if (shiftKey && selectionAnchorIndex !== null) {
          const startIndex = Math.min(selectionAnchorIndex, rowIndex);
          const endIndex = Math.max(selectionAnchorIndex, rowIndex);
          const rangeIds = conversations
            .slice(startIndex, endIndex + 1)
            .map((conversation) => conversation.id);

          for (const id of rangeIds) {
            if (checked) {
              next.add(id);
            } else {
              next.delete(id);
            }
          }
        } else if (checked) {
          next.add(conversationId);
        } else {
          next.delete(conversationId);
        }

        return [...next];
      });

      setSelectionAnchorIndex(rowIndex);
    },
    [conversations, selectionAnchorIndex],
  );

  const setAllConversationsSelected = useCallback(
    (checked: boolean) => {
      if (checked) {
        setRawSelectedConversationIds(
          conversations.map((conversation) => conversation.id),
        );
      } else {
        setRawSelectedConversationIds([]);
      }
      setSelectionAnchorIndex(null);
    },
    [conversations],
  );

  const clearSelectedConversations = useCallback(() => {
    setRawSelectedConversationIds([]);
    setSelectionAnchorIndex(null);
  }, []);

  return {
    selectedConversationIds,
    selectedConversations,
    selectedConversationCount,
    hoveredConversationId,
    hasAnySelectedConversations,
    setHoveredConversationId,
    isConversationSelected,
    setConversationSelected,
    setAllConversationsSelected,
    clearSelectedConversations,
  };
}
